/* eslint-disable max-lines */
import styled from '@emotion/styled';
import { useState } from 'react';
import { useRouter } from 'next/router';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import P1 from '@base/P1/P1';
import { getRelativeDayorCurrentDate } from '@utils/dates';
import H5 from '@base/H5/H5';
import H6 from '@base/H6/H6';
import Tags from '@base/Tags/Tags';
import P2 from '@base/P2/P2';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import Image from '@base/Image';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { segment } from '@actions/analytics';
import {
  DOW2_QUESTION_VIEWED,
  DOW2_SUBJECT_CHANGED_CLICKED,
  DOW2_TRANSCRIPT_VIEWED,
  DOW2_SOLUTION_CLICKED
} from '@constants/segment';
import useCustomDoubtsStore from '@stores/customDoubtsStore';
import {
  getSelectedDoubtSolutionSetter,
  getSubjectSelectionFlowSetter,
  getSubmittedFeedbacks
} from '@stores/selectors/customDoubtSelectors';
import { getSelectedDoubtSolution } from '@api/selectors/doubts';
import Paper from '@base/Paper/Paper';
import Menu from '@base/Menu/Menu';
import { useEventCallback } from '@material-ui/core';
import Dynamic from '@base/Dynamic/Dynamic';
import getTopicGroupAssetObj from '@cont/Syllabus/constants/getTopicGroupAssetObj';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { useIsMobile } from '@stores/AppCommonData';
import {
  DOUBT_SEGMENT_EVENT_SOURCE,
  FEEDBACK_INTENT,
  subjectSelectionState
} from './constants';
import Transcript from './Transcript';

const FullScreenImageDailog = Dynamic(() => import('./FullScreenImageDailog'));
const SubjectSelectionDrawer = Dynamic(() =>
  import('./SubjectSelectionDrawer')
);

const SideBarWrapper = styled.div`
  grid-column: 17/23;
  margin-top: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: ${Spacings.SPACING_8B};
  }
`;

const StylH5 = styled(H5)`
  margin-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    max-width: 360px;
  }
`;
const StylH6 = styled(H6)`
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.SMALL};
    line-height: ${Spacings.SPACING_18};
  }
`;

const DoubtSubject = styled(H5)`
  line-height: ${Spacings.SPACING_5B};
  margin-bottom: ${Spacings.SPACING_2};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.SMALL};
  }
`;

const DoubtDate = styled(P1)`
  color: var(--color-text-secondary);
  font-size: ${Fonts.ULTRA_SMALL};
`;
const SolutionsHeading = styled(H5)`
  margin-top: ${Spacings.SPACING_10B};
  margin-bottom: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    max-width: 360px;
    margin-top: ${Spacings.SPACING_0B};
  }
`;

const SolutionCard = styled.div`
  position: relative;
  height: 70px;
  width: 320px;
  padding: ${Spacings.SPACING_4B};
  border-radius: ${Spacings.SPACING_2B};
  cursor: pointer;
  display: flex;
  align-items: center;
  margin-bottom: ${Spacings.SPACING_2B};
  ${({ isSelected }) =>
    isSelected &&
    `
    background-color: var(--color-base-1);
    box-shadow: ${boxShadowGenerator()};

  `}
  ${mediaQuery.uptoMobile} {
    width: 100%;
    max-width: 360px;
  }
`;
const CardDataWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 38px;
`;

const CardDetails = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${Spacings.SPACING_1B};
`;

const ImageWrapper = styled.div`
  margin-right: ${Spacings.SPACING_3B};
  width: ${Spacings.SPACING_8B};
  height: ${Spacings.SPACING_8B};
`;

const LanguageTag = styled(Tags)`
  width: ${Spacings.SPACING_6B};
  height: ${Spacings.SPACING_4B};
  font-weight: ${Fonts.BOLD};
  font-size: ${Fonts.EXTRA_SMALL};
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_1B};
  padding: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-base-1);
`;

const CardTiming = styled(P2)`
  color: var(--color-text-secondary);
  margin-left: ${Spacings.SPACING_1B};
`;
const FeebackText = styled(P2)`
  color: ${({ $isGeneral }) =>
    $isGeneral ? 'var(--color-i-green)' : 'var(--color-text-secondary)'};
  margin-left: ${Spacings.SPACING_2};
`;

const BackgroundWrapper = styled.div`
  width: 100%;
  min-height: 48px;
  max-height: 144px;
  background-image: url('${(props) => props.bgImageSrc}');
  display: block;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  z-index: 1;
  filter: blur(64px);
  top: 0;
  left: 0;
  position: absolute;
`;

const DoubtImgWrapper = styled.div`
  min-height: 48px;
  max-height: 144px;
  display: flex;
  overflow: hidden;
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_1B};
  position: relative;
  align-items: center;
  justify-content: center;
`;

const DountImage = styled.img`
  width: 70%;
  object-fit: contain;
`;

const StyImage = styled(Image)`
  box-shadow: ${boxShadowGenerator()};
`;

const CardTag = styled.div`
  background-color: ${({ bgColor }) => bgColor};
  width: 45px;
  height: ${Spacings.SPACING_5B};
  padding: 6px ${Spacings.SPACING_2B};
  border-radius: ${Spacings.SPACING_1B};
  color: var(--color-base-1);
  font-size: ${Fonts.ULTRA_SMALL};
  font-weight: ${Fonts.BOLD};
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: ${Spacings.SPACING_4B};
  right: ${Spacings.SPACING_6B};
`;

export const DoubtImageWrapper = ({
  content,
  onClick,
  onMouseEnter,
  onMouseLeave,
  isImageHovered,
  handleQuestionViewed
}) => {
  return (
    <DoubtImgWrapper
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <BackgroundWrapper bgImageSrc={content} />
      <DountImage src={content} alt="doubt-image" />
      {isImageHovered && (
        <ExpandImageWrapper
          onClick={handleQuestionViewed}
          onMouseEnter={onMouseEnter}
        >
          <StyImage
            src="doubts20/expandIconCircular.svg"
            alt="expand-icon"
            width={24}
            height={24}
          />
        </ExpandImageWrapper>
      )}
    </DoubtImgWrapper>
  );
};

const getCardInnerText = (
  userFeedback,
  submittedFeedback,
  generalFeedback,
  isWatched,
  isUserSpecificViewAllowed
) => {
  if (
    isUserSpecificViewAllowed &&
    (userFeedback?.intent === FEEDBACK_INTENT.POSITIVE_FEEDBACK ||
      submittedFeedback === FEEDBACK_INTENT.POSITIVE_FEEDBACK)
  ) {
    const data = {
      text: 'You found it helpful',
      image: 'doubts20/thumbsUp.svg'
    };
    return data;
  }
  if (
    isUserSpecificViewAllowed &&
    (userFeedback?.intent === FEEDBACK_INTENT.NEGATIVE_FEEDBACK ||
      submittedFeedback === FEEDBACK_INTENT.NEGATIVE_FEEDBACK ||
      isWatched)
  ) {
    const data = {
      text: 'Watched'
    };
    return data;
  }
  if (generalFeedback?.value) {
    const data = {
      text: `${generalFeedback.value}% found this helpful`,
      isGeneral: true
    };
    return data;
  }
};

const getCardInfo = (
  languages,
  duration,
  generalFeedback,
  submittedFeedback,
  userFeedback,
  isWatched,
  isUserSpecificViewAllowed
) => {
  let durationValue = generalFeedback ? `${duration}` : duration;
  const cardData = getCardInnerText(
    userFeedback,
    submittedFeedback,
    generalFeedback,
    isWatched,
    isUserSpecificViewAllowed
  );
  if (durationValue && cardData?.text) durationValue = ` ${durationValue}`;

  return (
    <>
      <LanguageTag type="hollow">
        {languages?.[0]?.name?.slice(0, 2).toUpperCase()}
      </LanguageTag>
      <CardTiming>
        {durationValue} {cardData?.text ? '• ' : null}{' '}
      </CardTiming>
      <FeebackText $isGeneral={cardData?.isGeneral}>
        {cardData?.text}
      </FeebackText>
    </>
  );
};
const solutionsCards = (
  solutions = [],
  isDarkModeOn,
  hanldeCardClick,
  selectedSolutionUID,
  submittedFeedback,
  isUserSpecificViewAllowed
) => {
  const rendCard = solutions.map((solution) => {
    const {
      rank,
      languages,
      attachments,
      generalFeedback,
      uid,
      userFeedback,
      title,
      tags,
      isWatched
    } = solution;
    const duration = attachments?.[0]?.content?.duration?.string || '';

    const playIcon = isDarkModeOn
      ? 'doubts20/doubtPlayButtonDM.svg'
      : 'doubts20/doubtPlayButton.svg';

    const pauseIcon = isDarkModeOn
      ? 'doubts20/doubtPauseButtonDM.svg'
      : 'doubts20/doubtPauseButton.svg';

    const isSelectedSolution = selectedSolutionUID === uid;

    return (
      <SolutionCard
        key={rank}
        onClick={() => hanldeCardClick(uid, title)}
        isSelected={isSelectedSolution}
      >
        <ImageWrapper>
          <Image
            src={isSelectedSolution ? pauseIcon : playIcon}
            alt="doubt-play"
            width={32}
            height={32}
            onClick={() => {}}
          />
        </ImageWrapper>
        <CardDataWrapper>
          <StylH6>{title}</StylH6>
          <CardDetails>
            {getCardInfo(
              languages,
              duration,
              generalFeedback,
              submittedFeedback?.[uid],
              userFeedback,
              isWatched,
              isUserSpecificViewAllowed
            )}
          </CardDetails>
        </CardDataWrapper>
        {tags?.length > 0 ? (
          <CardTag bgColor={tags?.[0]?.color || 'var(--color-i-blue)'}>
            <span>NEW</span>
          </CardTag>
        ) : null}
      </SolutionCard>
    );
  });
  return <>{rendCard}</>;
};
const ExpandImageWrapper = styled.div`
  background: var(--color-base-1);
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  right: ${Spacings.SPACING_3B};
  right: 5px;
  bottom: 5px;
  z-index: 40;
  border-radius: 50%;
  box-shadow: ${boxShadowGenerator()};
`;
const ImagesWrapper = styled.div`
  position: relative;
  cursor: pointer;
`;

const DetailsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacings.SPACING_4B};
`;

const DetailsSubDateWrapper = styled.div`
  display: grid;
  grid-template-columns: 40px auto;
  grid-column-gap: ${Spacings.SPACING_4B};
`;

const Wrapper = styled(Paper)`
  width: 320px;
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_5B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    max-width: 360px;
    margin-bottom: ${Spacings.SPACING_8B};
  }
`;

const StyledMenu = styled(Menu)`
  &.MuiPopover-root .MuiPaper-root {
    .MuiList-root {
      padding: ${Spacings.SPACING_0B};
    }
  }
`;

const MenuButton = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_0B}${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
`;

export const MenuItemLabel = styled(H6)`
  font-weight: ${Fonts.SEMIBOLD};
  color: var(--color-text-primary);
  margin-left: ${Spacings.SPACING_2B};
  display: flex;
  align-items: center;
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.SMALL};
  }
`;

const SubjectImageWrapper = styled.div`
  position: relative;
  width: 40px;
  height: 40px;
  grid-column: 1/2;
  grid-row: 1/3;
`;
const IconWrapper = styled.div`
  position: relative;
  width: 20px;
  height: 20px;
`;

const CardMenuCTA = styled.div`
  cursor: pointer;
`;

const SideBar = ({
  doubtDetails,
  onSolutionClick,
  goalUID,
  threeDotsShown,
  setToolTip,
  isSubjectBeingChanged,
  setIsSubjectBeingChanged,
  revalidateDoubtDetails,
  isUserSpecificViewAllowed,
  ocrText
}) => {
  const router = useRouter();
  const [isImageHovered, setisImageHovered] = useState(false);
  const handleButtonEnter = () => {
    setisImageHovered(true);
  };
  const handleButtonLeave = () => {
    setisImageHovered(false);
  };

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  const handleClose = useEventCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);
  const {
    attachments = [],
    subject,
    timestamp,
    solutions,
    uid,
    tag
  } = doubtDetails || {};
  const updateSubjectSelectedState = useCustomDoubtsStore(
    getSubjectSelectionFlowSetter
  );
  const setSelectedDoubtSolution = useCustomDoubtsStore(
    getSelectedDoubtSolutionSetter
  );
  const selectedSolutionUID = useCustomDoubtsStore(getSelectedDoubtSolution);
  const isMobile = useIsMobile();

  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const hanldeCardClick = (clickedUID, title) => {
    setSelectedDoubtSolution(clickedUID);

    onSolutionClick(clickedUID);
    if (selectedSolutionUID !== clickedUID) {
      window?.scrollTo({
        top: isMobile ? 0 : 200,
        left: 0,
        behavior: 'smooth'
      });
    }

    segment.track(DOW2_SOLUTION_CLICKED, {
      Page_Url: router.asPath,
      source_type: DOUBT_SEGMENT_EVENT_SOURCE,
      solution_title: title
    });
  };
  const [dailogState, setDailogState] = useState(false);
  const [isTranscriptVisible, setIsTranscriptVisible] = useState(false);
  const submittedFeedback = useCustomDoubtsStore(getSubmittedFeedbacks(uid));
  const onCloseDialog = () => {
    setDailogState(false);
  };
  const imageContent = attachments.find(
    (content) =>
      content.contentType === 'IMAGE' || content.content_type === 'IMAGE'
  );
  const [subjectDrawer, setSubjectDrawer] = useState(false);
  // const [isSubjectBeingChanged, setIsSubjectBeingChanged] = useState(false);
  const hanldeSubjectChange = () => {
    revalidateDoubtDetails(goalUID, uid);
    setToolTip(true);
    setTimeout(() => {
      setToolTip(false);
    }, 3000);
    updateSubjectSelectedState(
      goalUID,
      uid,
      subjectSelectionState.afterRecevingSolution
    );
  };
  const routeGoalSegmentData = useGoalEventProps({ goalUID });
  const handlethreeDotsClicked = () => {
    setMenuAnchorEl(null);
    setSubjectDrawer(true);
    segment.track(DOW2_SUBJECT_CHANGED_CLICKED, {
      doubt_type: 'AAD',
      feature_version: 2,
      flow_type: 'Non_Demo',
      doubt_subject_selection: subjectSelectionState.afterRecevingSolution,
      ...routeGoalSegmentData
    });
  };

  const handleQuestionViewed = () => {
    setDailogState(true);
    segment.track(DOW2_QUESTION_VIEWED, {
      ...routeGoalSegmentData
    });
  };
  const [threeDotsImage, setThreeDotsImage] = useState(
    'doubts20/three_dots.svg'
  );

  const threeDotsHover = () => {
    setThreeDotsImage('doubts20/circularThreeDot.svg');
  };
  const threeDotsUnHover = () => {
    setThreeDotsImage('doubts20/three_dots.svg');
  };

  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
    segment.track(DOW2_TRANSCRIPT_VIEWED, {
      Page_Url: router.asPath,
      source_type: DOUBT_SEGMENT_EVENT_SOURCE,
      button_name: 'Kebab'
    });
  };

  const handleShowTranscript = () => {
    setMenuAnchorEl(null);
    setIsTranscriptVisible(true);
    segment.track(DOW2_TRANSCRIPT_VIEWED, {
      Page_Url: router.asPath,
      source_type: DOUBT_SEGMENT_EVENT_SOURCE,
      button_name: 'View Transcipt'
    });
  };

  const handleHideTranscript = () => {
    setMenuAnchorEl(null);
    setIsTranscriptVisible(false);
  };

  const transcriptIcon = isDarkModeOn
    ? 'doubts20/transcriptIconDM.svg'
    : 'doubts20/transcriptIcon.svg';

  const changeSubjectIcon = isDarkModeOn
    ? 'doubts20/changeSubjectDM.svg'
    : 'doubts20/changeSubject.svg';

  const { icon } = getTopicGroupAssetObj(subject?.name);

  const shouldShowChangeSubjectAction =
    isLoggedIn && threeDotsShown && !isSubjectBeingChanged;
  const shouldShowViewTranscriptAction = !isTranscriptVisible && ocrText;
  const shouldShowHideTransciptAction = isTranscriptVisible && ocrText;
  const shouldShowCardMenuCTA =
    shouldShowChangeSubjectAction ||
    shouldShowViewTranscriptAction ||
    shouldShowHideTransciptAction;

  return (
    <>
      <SideBarWrapper>
        <StylH5>Doubt details</StylH5>
        <Wrapper>
          <DetailsWrapper>
            <DetailsSubDateWrapper>
              <SubjectImageWrapper>
                <Image src={icon} alt="subject-icon" layout="fill" />
              </SubjectImageWrapper>
              <DoubtSubject as="h1">{subject?.name}</DoubtSubject>
              {subject?.uid ? (
                <DoubtDate>
                  {getRelativeDayorCurrentDate(timestamp)}{' '}
                  {tag ? `• ${tag}` : null}
                </DoubtDate>
              ) : null}
            </DetailsSubDateWrapper>
            <StyledMenu
              anchorEl={menuAnchorEl}
              onClose={handleClose}
              position="left"
              width="166px"
            >
              {shouldShowChangeSubjectAction && (
                <MenuButton
                  onClick={handlethreeDotsClicked}
                  className="menuButton"
                >
                  <IconWrapper>
                    <Image
                      src={changeSubjectIcon}
                      alt="change-subject"
                      layout="fill"
                    />
                  </IconWrapper>
                  <MenuItemLabel>Change subject</MenuItemLabel>
                </MenuButton>
              )}
              {shouldShowViewTranscriptAction && (
                <MenuButton
                  onClick={handleShowTranscript}
                  className="menuButton"
                >
                  <IconWrapper>
                    <Image
                      src={transcriptIcon}
                      alt="view-transcript"
                      layout="fill"
                    />
                  </IconWrapper>
                  <MenuItemLabel>View transcript</MenuItemLabel>
                </MenuButton>
              )}
              {shouldShowHideTransciptAction && (
                <MenuButton
                  onClick={handleHideTranscript}
                  className="menuButton"
                >
                  <IconWrapper>
                    <Image
                      src={transcriptIcon}
                      alt="hide-transcript"
                      layout="fill"
                    />
                  </IconWrapper>
                  <MenuItemLabel>Hide transcript</MenuItemLabel>
                </MenuButton>
              )}
            </StyledMenu>
            {shouldShowCardMenuCTA && (
              <CardMenuCTA
                onClick={handleMenuClick}
                onMouseEnter={threeDotsHover}
                onMouseLeave={threeDotsUnHover}
              >
                <Image
                  src={threeDotsImage}
                  alt="Choose Subject"
                  width={40}
                  height={40}
                />
              </CardMenuCTA>
            )}
          </DetailsWrapper>
          <Transcript ocrText={ocrText} isVisible={isTranscriptVisible} />
          <ImagesWrapper>
            <DoubtImageWrapper
              onClick={handleQuestionViewed}
              onMouseEnter={handleButtonEnter}
              onMouseLeave={handleButtonLeave}
              content={imageContent?.content}
              isImageHovered={isImageHovered}
              isDarkModeOn={isDarkModeOn}
              handleQuestionViewed={handleQuestionViewed}
            />
          </ImagesWrapper>
          {dailogState && (
            <FullScreenImageDailog
              dialogState={dailogState}
              attachments={attachments}
              onClose={onCloseDialog}
            />
          )}
        </Wrapper>
        {solutions?.length > 0 && subject?.uid && (
          <SolutionsHeading>Solutions available</SolutionsHeading>
        )}
        {subject?.uid
          ? solutionsCards(
              solutions,
              isDarkModeOn,
              hanldeCardClick,
              selectedSolutionUID,
              submittedFeedback,
              isUserSpecificViewAllowed
            )
          : null}
        {isLoggedIn && !!subjectDrawer && (
          <SubjectSelectionDrawer
            setIsSubjectBeingChanged={setIsSubjectBeingChanged}
            subjectDrawer={subjectDrawer}
            selectedSubject={subject}
            setSubjectDrawer={setSubjectDrawer}
            doubtUID={uid}
            goalUID={goalUID}
            onComplete={hanldeSubjectChange}
            state={subjectSelectionState.afterRecevingSolution}
            mainHeading="Your doubt subject"
            subHeading="You can change the subject once if it is incorrect"
          />
        )}
      </SideBarWrapper>
    </>
  );
};

export default SideBar;
